import { Form as InertiaForm } from '@inertiajs/react'
import { type ComponentProps } from 'react'

import { DeviceAgentOption, DeviceType } from './types.ts'

type FormProps = Omit<ComponentProps<typeof InertiaForm>, 'children'> & {
  device: DeviceType
  submitText: string
  agents?: DeviceAgentOption[]
}

export default function Form({
  device,
  submitText,
  agents = [],
  ...formProps
}: FormProps) {
  const statusOptions = ['assigned', 'active', 'inactive']
  const selectedStatus = device.status || 'assigned'
  const formatStatus = (status: string) => {
    if (status === 'assigned') return 'Assigné'
    if (status === 'active') return 'Actif'
    if (status === 'inactive') return 'Inactif'
    return status
  }

  return (
    <InertiaForm
      transform={data => ({ device: data })}
      className="contents"
      {...formProps}
    >
      {({ errors, processing }) => (
        <div className={"grid gap-6 md:grid-cols-3"}>
          <div className="space-y-6 md:col-span-2">
          <div className="my-5">
            <label htmlFor="name">Nom</label>
            <input
              type="text"
              name="name"
              id="name"
              defaultValue={device.name}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            />
            {errors.name && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.name.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="user_id">Agent</label>
            <select
              name="user_id"
              id="user_id"
              defaultValue={device.user_id || ""}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full bg-background"
            >
              {agents.length > 0 ? (
                <option value="">Sélectionner un agent</option>
              ) : (
                <option value="" disabled>
                  Aucun agent disponible
                </option>
              )}
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.code ? `${agent.full_name} (${agent.code})` : agent.full_name}
                </option>
              ))}
            </select>
            {errors.user_id && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.user_id.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="serial_number">Numéro de série</label>
            <input
              type="text"
              name="serial_number"
              id="serial_number"
              defaultValue={device.serial_number}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            />
            {errors.serial_number && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.serial_number.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="imei">IMEI</label>
            <input
              type="text"
              name="imei"
              id="imei"
              defaultValue={device.imei}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            />
            {errors.imei && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.imei.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="status">Statut</label>
            <select
              name="status"
              id="status"
              defaultValue={device.status}
              className="block shadow rounded-md border border-gray-400 bg-background outline-none px-3 py-2 mt-2 w-full"
            >
              {!statusOptions.includes(selectedStatus) && (
                <option value={selectedStatus}>{formatStatus(selectedStatus)}</option>
              )}
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {formatStatus(status)}
                </option>
              ))}
            </select>
            {errors.status && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.status.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="active">Actif</label>
            <input
              type="checkbox"
              name="active"
              id="active"
              defaultChecked={device.active}
              className="block mt-2 h-5 w-5"
            />
            {errors.active && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.active.join(', ')}
              </div>
            )}
          </div>

          <div className="inline">
            <button
              type="submit"
              disabled={processing}
              className="rounded-lg py-3 px-5 bg-blue-600 text-white inline-block font-medium cursor-pointer"
            >
              {submitText}
            </button>
          </div>
          </div>
        </div>
      )}
    </InertiaForm>
  )
}
